import { FieldWrapper, getFieldErrorMessage } from "@birdwatch/ui/form";
import { type ReactNode, useState } from "react";
import { PriceInput } from "@/components/ui/input/price-input";
import {
  type FieldBindingProps,
  PolyField,
  pickBinding,
} from "@/fields/_internal/poly-field";
import { parseDecimalInput, resolveDecimalDisplay } from "./decimal-buffer";

type DecimalFieldBaseProps = {
  label?: ReactNode;
  description?: ReactNode;
  optional?: boolean;
  disabled?: boolean;
  /** Applied to the FieldWrapper root. */
  className?: string;
  /** Applied to the inner PriceInput. */
  inputClassName?: string;
  placeholder?: string;
  allowNegative?: boolean;
  clearable?: boolean;
  autoFocus?: boolean;
  suffix?: ReactNode;
  suffixClassName?: string;
  onBlur?: () => void;
  /** Fired AFTER the value is committed — for cross-field recomputation. */
  onValueChange?: (value: number | null) => void;
  "data-test"?: string;
};

export type DecimalFieldProps = DecimalFieldBaseProps & FieldBindingProps;

type DecimalInputProps = {
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur: () => void;
  disabled?: boolean;
  inputClassName?: string;
  placeholder?: string;
  allowNegative?: boolean;
  clearable?: boolean;
  autoFocus?: boolean;
  suffix?: ReactNode;
  suffixClassName?: string;
  "data-test"?: string;
};

/**
 * Declared at module scope on purpose: an inner component defined inside
 * {@link DecimalField} would be a new component type on every render, remounting
 * the input and dropping focus and caret after every keystroke.
 */
function DecimalInput({
  value,
  onChange,
  onBlur,
  disabled,
  inputClassName,
  placeholder,
  allowNegative = false,
  clearable,
  autoFocus,
  suffix,
  suffixClassName,
  "data-test": dataTest,
}: DecimalInputProps) {
  // The last string the user typed. Authoritative only while it still parses to
  // the value the form holds — see `resolveDecimalDisplay`.
  const [buffer, setBuffer] = useState("");

  return (
    <PriceInput
      data-test={dataTest}
      value={resolveDecimalDisplay(buffer, value)}
      onChange={(next) => {
        // PriceInput's own `onChange` intersects the DOM input handler it
        // spreads onto, so the parameter widens to include a ChangeEvent. At
        // runtime it only ever emits the validated raw string.
        const raw =
          typeof next === "string" || typeof next === "number"
            ? String(next)
            : "";
        setBuffer(raw);
        onChange(parseDecimalInput(raw));
      }}
      onBlur={onBlur}
      allowNegative={allowNegative}
      placeholder={placeholder}
      className={inputClassName}
      suffix={suffix}
      suffixClassName={suffixClassName}
      clearable={clearable}
      autoFocus={autoFocus}
      disabled={disabled}
    />
  );
}

/**
 * A non-currency decimal number (quantities, hours, rates). Value contract:
 * `number | null` — an emptied input stores `null`, so the consumer schema must
 * be `.nullable()` unless empty is genuinely invalid.
 *
 * The input keeps the user's raw text for as long as it still means the stored
 * number, so a half-typed decimal (`"1."`, `"1.50"`) survives to the next
 * keystroke instead of being flattened by `String(Number(raw))`.
 *
 * Use `MoneyField` for currency/percent amounts (it owns the `$`/`%` affixes).
 */
export function DecimalField(props: DecimalFieldProps) {
  const {
    label,
    description,
    optional,
    disabled,
    className,
    inputClassName,
    placeholder,
    allowNegative = false,
    clearable,
    autoFocus,
    suffix,
    suffixClassName,
    onBlur,
    onValueChange,
  } = props;
  const binding = pickBinding(props);
  return (
    <PolyField<number | null> {...binding}>
      {(field) => (
        <FieldWrapper
          label={label}
          description={description}
          optional={optional}
          error={getFieldErrorMessage(field)}
          className={className}
          disabled={disabled}
        >
          <DecimalInput
            data-test={props["data-test"]}
            value={field.state.value}
            onChange={(next) => {
              field.handleChange(next);
              onValueChange?.(next);
            }}
            onBlur={() => {
              field.handleBlur();
              onBlur?.();
            }}
            allowNegative={allowNegative}
            placeholder={placeholder}
            inputClassName={inputClassName}
            suffix={suffix}
            suffixClassName={suffixClassName}
            clearable={clearable}
            autoFocus={autoFocus}
            disabled={disabled}
          />
        </FieldWrapper>
      )}
    </PolyField>
  );
}
